/**
 * The `SurfaceType.WorkflowEditor` host: a draft editor over a full-width canvas that knows nothing about the domain it edits.
 * The kit (`WORKFLOW_EDITOR`) supplies the canvas, the item sheet, panels, validation and statuses; the host owns the chrome:
 * - Header: title, status, revision, optional `editor.headerActions`, settings drawer button, undo/redo, issues button,
 *   actions, Save, Close.
 * - Levels over the canvas (never sibling tabs): the settings drawer (`$settingsOpen`, the schema's `rail` sections), the
 *   issues popover (`$issuesOpen`), the kit's item sheet while an item is selected (`$selection`), and on top the side
 *   panel (`$sidePanel`), a kit component the header actions open through `toggleSidePanel`.
 * State: the selection, the open side panel, the undo history and the draft snapshot taken when an item opened.
 * Invariant: every draft change goes through the page (`writeBinds`), so dirty state, Save and discard see exactly what the
 * canvas shows; the item Form writes through live, and Cancel restores the snapshot from when the item opened.
 */
import { DOCUMENT, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked, ViewEncapsulation, type Type } from '@angular/core';
import { ButtonComponent, ButtonDisplayStyle, ButtonIconComponent, ButtonSize } from '@workbuddy/ui/v2/components/button';
import { commandKeymap, formatChord, humanizeSemanticId, SemanticCommand, ShortcutScope, useShortcuts, useShortcutScope } from '@workbuddy/ui/services/keyboard.shortcuts';
import { ActionBarComponent } from '@workbuddy/ui/v2/components/action.bar';
import { EntityDialogs } from '@workbuddy/ui/v2/blocks/entity.dialog/entity.dialog.contracts';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import type { FieldError, FieldMode } from '../../core/contracts/field.binding';
import type { AnySurfaceRuntime } from '../../core/contracts/surface.runtime';
import type { SurfaceContextHandle } from '../../core/runtime/surface.context/create.surface.context.options';
import { readModelValue } from '../../core/runtime/model.path';
import { defineSurfaceRuntime } from '../../core/runtime/surface.runtime/define.surface.runtime';
import { WbSectionComponent } from '../../core/sections/wb.section.component';
import { resolveSurfaceActions, toSurfaceActionCommands } from '../../core/surface/surface.action.presenter';
import { resolveSurfaceBanner } from '../../core/surface/surface.chrome.presenter';
import { buildSectionRenderMap, visibleSectionsForPaths } from '../../core/surface/surface.section.presenter';
import { defineSurface } from '../../core/surface/wb.surface.define';
import { collectFormHostErrors, isFormHostBusy, isFormHostSubmitDisabled } from '../form/form.host.state';
import { emptyDraftHistory, recordDraft, redoDraft, undoDraft } from './workflow.editor.history';
import type { DraftHistory } from './workflow.editor.history';
import type { WorkflowEditorCanvasInputs, WorkflowEditorHeaderActionsInputs, WorkflowEditorIssue, WorkflowEditorItemStatus, WorkflowEditorMountedPanel, WorkflowEditorPanel, WorkflowEditorSidePanelInputs, WorkflowEditorStepSheetInputs } from './workflow.editor.host.contract';
import { WORKFLOW_EDITOR } from './workflow.editor.surface.definition';
import { workflowEditorHostBridgeStyles, workflowEditorHostStyleId } from './wb.surface.workflow.editor.host.theme';

type Draft = Record<string, unknown>;

const workflowEditorShortcutScope: ShortcutScope = { kind: 'panel', panelId: 'surface-workflow-editor' };

/** Feature-neutral host for draft-based flow editors; see the file header for what it owns. */
@Component({
  selector: 'wb-surface-workflow-editor-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgComponentOutlet, NgTemplateOutlet, ButtonComponent, ButtonIconComponent, ActionBarComponent, WbSectionComponent],
  templateUrl: './wb.surface.workflow.editor.host.component.html',
  styleUrl: './wb.surface.workflow.editor.host.component.less',
  host: {
    '[attr.data-surface]': '"workflowEditor"',
    '[attr.aria-busy]': '$busy()',
    '[class.sheet-open]': '$selection() !== null',
    '[class.settings-drawer-open]': '$settingsOpen()',
    '[class.issues-popover-open]': '$issuesOpen()',
    '(keydown)': 'onKeydown($event)',
  },
})
export class WbSurfaceWorkflowEditorHostComponent {

  private readonly document = inject(DOCUMENT);
  protected readonly editor = inject(WORKFLOW_EDITOR);
  private readonly entityDialogs = inject(EntityDialogs, { optional: true });
  private readonly isMac = /Mac|iPod|iPhone|iPad/i.test(this.document.defaultView?.navigator.platform ?? '');

  public readonly $handle = input.required<SurfaceContextHandle>({ alias: 'handle' });

  protected readonly ButtonDisplayStyle = ButtonDisplayStyle;
  protected readonly ButtonSize = ButtonSize;
  protected readonly $selection = signal<string | null>(null);
  protected readonly $settingsOpen = signal(false);
  protected readonly $issuesOpen = signal(false);
  // The kit component open in the side panel; the header actions toggle it, the host only shows it.
  protected readonly $sidePanel = signal<Type<unknown> | null>(null);
  protected readonly $submitting = signal(false);
  protected readonly $confirmDiscard = signal(false);
  private readonly $submitErrors = signal<readonly FieldError[]>([]);
  private readonly $history = signal<DraftHistory<Draft>>(emptyDraftHistory());
  // The draft as last recorded; a model change that differs from it is an edit and becomes an undo step.
  private lastSnapshot: string | undefined;
  // The draft snapshot when the current item was selected, so Cancel restores any live write-through edits.
  private selectionBaseDraft: Draft | null = null;
  private selectionWasDirty = false;
  // Moves on every draft write that did not come from the open Form, so `$form` remounts over the draft as it now is.
  private readonly $formGeneration = signal(0);

  protected readonly $page = computed(() => this.$handle().page);
  protected readonly $draft = computed(() => this.$page().$model());
  protected readonly $busy = computed(() => isFormHostBusy({ initializing: this.$page().$initializing(), submitting: this.$submitting(), saving: this.$page().$saving() }));
  protected readonly $readonly = computed(() => this.$page().$readonly());
  protected readonly $surfaceMode = computed<FieldMode>(() => this.$readonly() || this.$busy() ? 'read' : 'edit');
  protected readonly $submitDisabled = computed(() => isFormHostSubmitDisabled({ readonly: this.$readonly(), busy: this.$busy(), invalid: this.$page().$invalid(), validating: this.$page().$validating() }));
  protected readonly $submitLabel = computed(() => this.$handle().compiled.submitLabel ?? 'Save');
  protected readonly $submitTooltip = computed(() => `${this.$submitLabel()} · ${formatChord(commandKeymap[SemanticCommand.Submit], this.isMac)}`);
  protected readonly $undoTooltip = `Undo · ${formatChord(commandKeymap[SemanticCommand.Undo], this.isMac)}`;
  protected readonly $redoTooltip = `Redo · ${formatChord(commandKeymap[SemanticCommand.Redo], this.isMac)}`;
  protected readonly $title = computed(() => {

    const header = this.$handle().compiled.header;
    const value = header ? readModelValue(this.$draft(), header.identifier) : undefined;
    return value == null || value === '' ? header?.kind ?? 'Untitled' : String(value);
  });
  // The settings drawer is named after the record kind (the schema header's `kind`), such as "Workflow".
  protected readonly $settingsLabel = computed(() => this.$handle().compiled.header?.kind ?? 'Settings');
  protected readonly $revision = computed(() => this.editor.revision === null ? null : `r${this.editor.revision}`);
  protected readonly $errors = computed(() => collectFormHostErrors(this.$page().$surfaceErrors(), this.$submitErrors()));
  protected readonly $banner = computed(() => resolveSurfaceBanner(this.$handle().resolver));
  protected readonly $actions = computed(() => toSurfaceActionCommands(resolveSurfaceActions(this.$handle()), this.$busy()));
  protected readonly $canUndo = computed(() => !this.$readonly() && !this.$busy() && this.$history().past.length > 0);
  protected readonly $canRedo = computed(() => !this.$readonly() && !this.$busy() && this.$history().future.length > 0);

  protected readonly $issues = computed<readonly WorkflowEditorIssue[]>(() => this.editor.validate(this.$draft()));
  private readonly $flagged = computed(() => new Set(this.$issues().map(issue => this.editor.select(this.$draft(), issue.path)).filter((selection): selection is string => selection !== null)));
  protected readonly $testStatuses = computed<ReadonlyMap<string, WorkflowEditorItemStatus>>(() => this.editor.statuses?.(this.$draft()) ?? new Map());

  protected readonly $canvasInputs = computed((): WorkflowEditorCanvasInputs => ({
    draft: this.$draft(),
    selection: this.$selection(),
    flagged: this.$flagged(),
    testStatuses: this.$testStatuses(),
    select: selection => this.select(selection),
    settingsOpen: this.$settingsOpen(),
    openSettings: () => this.showSettings(),
    change: draft => this.editDraft(draft),
  }));

  protected readonly $headerInputs = computed((): WorkflowEditorHeaderActionsInputs => ({
    draft: this.$draft(),
    readonly: this.$readonly(),
    sidePanel: this.$sidePanel(),
    toggleSidePanel: component => this.$sidePanel.update(open => open === component ? null : component),
  }));

  protected readonly $sidePanelInputs = computed((): WorkflowEditorSidePanelInputs => ({
    draft: this.$draft(),
    close: () => this.$sidePanel.set(null),
  }));

  private readonly $sectionMap = computed(() => buildSectionRenderMap(this.$handle()));
  protected readonly $settingsSections = computed(() => visibleSectionsForPaths(this.$handle().compiled.namedSlotSections['rail'] ?? [], this.$sectionMap(), this.$page()));

  private readonly $orderedIds = computed<readonly string[]>(() => this.editor.orderedIds?.(this.$draft()) ?? []);
  // The selection's index in `$orderedIds`; -1 when nothing is selected or the kit gives no order.
  private readonly $orderIndex = computed(() => {

    const selection = this.$selection();
    return selection === null ? -1 : this.$orderedIds().indexOf(selection);
  });
  private readonly $positionLabel = computed(() => this.$orderIndex() >= 0 ? `${this.$orderIndex() + 1} of ${this.$orderedIds().length}` : null);
  private readonly $previousId = computed(() => this.$orderIndex() > 0 ? this.$orderedIds()[this.$orderIndex() - 1] : null);
  private readonly $nextId = computed(() => this.$orderIndex() >= 0 && this.$orderIndex() < this.$orderedIds().length - 1 ? this.$orderedIds()[this.$orderIndex() + 1] : null);

  /**
   * The selected item's mounted Form. It is rebuilt when the selection changes or `$formGeneration` moves (any draft write
   * that did not come from the Form itself), so live write-through never remounts it and typing keeps focus.
   */
  private readonly $form = computed<{ selection: string; definition: WorkflowEditorMountedPanel['definition'] } | null>(() => {

    const selection = this.$selection();
    this.$formGeneration();
    if (selection === null) {
      return null;
    }
    const panel = untracked(() => this.editor.panelFor(this.$draft(), selection));
    if (!panel) {
      return null;
    }
    return {
      selection,
      definition: this.mountForm(selection, panel, untracked(() => this.$readonly())),
    };
  });

  /** The selected item's panel as the kit describes it now, with the mounted Form kept across write-through. */
  protected readonly $panel = computed<WorkflowEditorMountedPanel | null>(() => {

    const form = this.$form();
    if (!form) {
      return null;
    }
    const panel = this.editor.panelFor(this.$draft(), form.selection);
    if (!panel) {
      return null;
    }
    return {
      ...panel,
      selection: form.selection,
      definition: form.definition,
    };
  });

  protected readonly $sheetInputs = computed<WorkflowEditorStepSheetInputs | null>(() => {

    const panel = this.$panel();
    if (!panel) {
      return null;
    }
    return {
      draft: this.$draft(),
      selection: panel.selection,
      panel,
      readonly: this.$readonly() || this.$busy(),
      positionLabel: this.$positionLabel(),
      previousId: this.$previousId(),
      nextId: this.$nextId(),
      select: selection => this.select(selection),
      rename: name => this.renameSelection(name),
      change: draft => this.editDraft(draft),
    };
  });

  public constructor() {

    DesignSystem.Mount(this.document, workflowEditorHostStyleId, workflowEditorHostBridgeStyles);
    this.recordEdits();
    this.bindShortcuts();
  }

  protected onKeydown(event: KeyboardEvent): void {

    // Escape peels the topmost overlay first (side panel → item sheet → issues popover → settings drawer), then asks to close the editor.
    if (event.defaultPrevented || event.key !== 'Escape') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (this.$sidePanel() !== null) {
      this.$sidePanel.set(null);
    } else if (this.$selection() !== null) {
      this.clearSelection();
    } else if (this.$issuesOpen()) {
      this.$issuesOpen.set(false);
    } else if (this.$settingsOpen()) {
      this.$settingsOpen.set(false);
    } else {
      this.close();
    }
  }

  /** Opens `selection`'s sheet over the canvas with a fresh Form; null closes the sheet. */
  private select(selection: string | null): void {

    this.$formGeneration.update(generation => generation + 1);
    if (selection !== null) {
      this.selectionBaseDraft = structuredClone(this.$draft());
      this.selectionWasDirty = this.$page().$dirty();
      this.$issuesOpen.set(false);
      this.$settingsOpen.set(false);
    } else {
      this.selectionBaseDraft = null;
    }
    this.$selection.set(selection);
  }

  /** Renames the selected item through `editor.rename`; the open Form stays mounted, like its own write-through. */
  private renameSelection(name: string): void {

    const selection = this.$selection();
    if (!selection || !this.editor.rename || this.$readonly() || this.$busy()) {
      return;
    }
    this.writeDraft(this.editor.rename(this.$draft(), selection, name));
  }

  /** Selects the item an issue points at; an issue on the record itself opens the settings drawer. */
  protected openIssue(issue: WorkflowEditorIssue): void {

    this.$issuesOpen.set(false);
    const target = this.editor.select(this.$draft(), issue.path);
    if (target !== null) {
      this.select(target);
    } else {
      this.showSettings();
    }
  }

  /** Toggles the issues popover from the header's issue count button. */
  protected toggleIssues(): void {

    const next = !this.$issuesOpen();
    this.$issuesOpen.set(next);
    if (next) {
      this.$settingsOpen.set(false);
    }
  }

  /** Toggles the settings drawer from the header's drawer button. */
  protected toggleSettings(): void {

    const next = !this.$settingsOpen();
    if (next) {
      this.$selection.set(null);
      this.$issuesOpen.set(false);
    }
    this.$settingsOpen.set(next);
  }

  /** Opens the settings drawer, closing the sheet and the issues popover. */
  private showSettings(): void {

    this.$selection.set(null);
    this.$issuesOpen.set(false);
    this.$settingsOpen.set(true);
  }

  /** Closes the sheet and keeps any applied or written-through edits. */
  private clearSelection(): void {

    this.selectionBaseDraft = null;
    this.$selection.set(null);
  }

  /** Cancels the item Form, restoring the draft snapshot from when the item was selected if write-through changed it. */
  private cancelSelection(): void {

    const base = this.selectionBaseDraft;
    const wasDirty = this.selectionWasDirty;
    this.selectionBaseDraft = null;
    if (base && JSON.stringify(this.$draft()) !== JSON.stringify(base)) {
      if (!wasDirty) {
        this.$page().reset();
      } else {
        this.writeDraft(base);
      }
    }
    this.$selection.set(null);
  }

  /**
   * Close. Inside a routed entity dialog the dialog's leave check (Save / Discard / Keep editing) owns the question, so asking
   * here too would prompt twice; standalone, the host asks inline.
   */
  protected close(): void {

    if (this.entityDialogs?.$active()) {
      void this.entityDialogs.close();
      return;
    }
    if (this.$page().$dirty() && !this.$readonly()) {
      this.$confirmDiscard.set(true);
    } else {
      this.discard();
    }
  }

  protected discard(): void {

    this.$confirmDiscard.set(false);
    this.$page().cancel();
  }

  protected async submit(): Promise<void> {

    if (this.$submitDisabled()) {
      return;
    }
    this.$confirmDiscard.set(false);
    this.$submitting.set(true);
    this.$submitErrors.set([]);
    try {
      const result = await this.$page().submit();
      if (!result.ok) {
        this.$submitErrors.set(result.errors ?? []);
      }
    } finally {
      this.$submitting.set(false);
    }
  }

  protected undo(): void {

    if (!this.$canUndo()) {
      return;
    }
    const step = undoDraft(this.$history(), structuredClone(this.$draft()));
    if (step) {
      this.restore(step.draft, step.history);
    }
  }

  protected redo(): void {

    if (!this.$canRedo()) {
      return;
    }
    const step = redoDraft(this.$history(), structuredClone(this.$draft()));
    if (step) {
      this.restore(step.draft, step.history);
    }
  }

  /** Builds the item Form for `panel`: Apply labelled, every edit written through into the draft, Cancel restoring it. */
  private mountForm(selection: string, panel: WorkflowEditorPanel, readonly: boolean): WorkflowEditorMountedPanel['definition'] {

    const baseRuntime = panel.runtime;
    const liveRuntime = defineSurfaceRuntime<Draft>({
      ...(baseRuntime?.config ?? {}),
      onModelChange: (event, page) => {
        baseRuntime?.config.onModelChange?.(event, page);
        this.writeThrough(selection, page.$model());
      },
    }) as AnySurfaceRuntime;
    return defineSurface<Draft>({
      schema: { ...panel.schema, submitLabel: 'Apply' },
      runtime: liveRuntime,
      initialModel: structuredClone(panel.item) as Draft,
      initialReadonly: readonly,
      onSubmit: event => this.applyPanel(selection, event.model as Draft),
      onCancel: () => this.cancelSelection(),
    });
  }

  /** Writes each item Form edit into the draft at once, keeping that Form mounted. */
  private writeThrough(selection: string, edited: Draft): void {

    if (this.$readonly() || this.$page().$saving() || this.$selection() !== selection) {
      return;
    }
    try {
      this.writeDraft(this.editor.apply(this.$draft(), selection, edited));
    } catch {
      // A half-typed item the kit cannot apply yet stays in the Form until the next edit.
    }
  }

  /**
   * Writes an applied item into the draft and makes it Cancel's new restore point. A read-only or saving draft refuses, and
   * so does a selection the draft no longer holds (the Form would write into nothing).
   */
  private applyPanel(selection: string, edited: Draft): Draft {

    if (this.$readonly() || this.$page().$saving()) {
      throw new Error('This draft is no longer editable.');
    }
    if (!this.editor.panelFor(this.$draft(), selection)) {
      throw new Error('This item is no longer in the draft. Select it again before applying.');
    }
    const next = this.editor.apply(this.$draft(), selection, edited);
    this.selectionBaseDraft = structuredClone(next);
    this.selectionWasDirty = true;
    this.writeDraft(next);
    return edited;
  }

  private restore(draft: Draft, history: DraftHistory<Draft>): void {

    // Marking the restored draft as seen keeps the recording effect from treating the restore as a new edit.
    this.lastSnapshot = JSON.stringify(draft);
    this.$history.set(history);
    this.replaceDraft(draft);
  }

  /** A whole-draft edit from the canvas or the sheet; like Apply it goes through the page, and a read-only or busy draft ignores it. */
  private editDraft(next: Draft): void {

    if (this.$surfaceMode() === 'edit') {
      this.replaceDraft(next);
    }
  }

  /** Writes a draft that did not come from the open Form (canvas, sheet, undo), so the Form remounts over it. */
  private replaceDraft(next: Draft): void {

    this.writeDraft(next);
    this.$formGeneration.update(generation => generation + 1);
  }

  /** Writes only the top-level binds that differ, so dirty tracking and field change events stay per bind. */
  private writeDraft(next: Draft): void {

    const current = this.$draft();
    const changed = Object.fromEntries(Object.entries(next).filter(([key, value]) => JSON.stringify(current[key]) !== JSON.stringify(value)));
    if (Object.keys(changed).length) {
      this.$page().writeBinds(changed);
    }
  }

  private recordEdits(): void {

    effect(() => {
      const draft = this.$draft();
      const snapshot = JSON.stringify(draft);
      untracked(() => {
        if (this.lastSnapshot !== undefined && snapshot !== this.lastSnapshot) {
          this.$history.update(history => recordDraft(history, JSON.parse(this.lastSnapshot!) as Draft));
        }
        this.lastSnapshot = snapshot;
      });
    });
  }

  /** Save, Undo and Redo bind through the central keymap; Save also fires from inside a text field. */
  private bindShortcuts(): void {

    useShortcutScope(workflowEditorShortcutScope);
    const bindings = [
      { semanticId: SemanticCommand.Submit, allowInInputs: true, run: () => void this.submit() },
      { semanticId: SemanticCommand.Undo, allowInInputs: false, run: () => this.undo() },
      { semanticId: SemanticCommand.Redo, allowInInputs: false, run: () => this.redo() },
    ];
    useShortcuts(bindings.map(binding => ({
      id: `workflow-editor.${binding.semanticId}`,
      keys: commandKeymap[binding.semanticId],
      label: humanizeSemanticId(binding.semanticId),
      scope: workflowEditorShortcutScope,
      allowInInputs: binding.allowInInputs,
      action: binding.run,
    })));
  }
}
